import IReportsClient from "@/interfaces/api/reportsClient";
import { ICancelTariffResponse, IGetReportsResponse, IPaymentStatusResponse, ISignUpReportResponse, ITariffPrices, IUserTariffResponse, TariffPeriod } from "@/interfaces/reports";
import { IFilters } from "@/interfaces/search";

class ReportsClient implements IReportsClient {
  async getReport(query?: string, filters?: IFilters): Promise<IGetReportsResponse> {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('No token')
    }

    const params = new URLSearchParams({
      ...(query ? { query: query } : {})
    });
    if(filters) {
      (Object.keys(filters) as Array<keyof IFilters>).forEach((key) => {
        const value = filters[key];
        if(value !== null && value !== undefined) {
          if (value instanceof Date) {
            params.append(key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), value.toISOString().split('T')[0]);
          } else {
            params.append(key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), String(value));
          }
        }
      });
    }

    try {
      const response = await fetch(`/api/report/?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const data = await response.json();
      return data as IGetReportsResponse
  
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return {status: "ERROR", task_id: null} as IGetReportsResponse
    }
  }
  async getPrices(): Promise<ITariffPrices | null> {
    try {
      const response = await fetch(`/api/tariff/prices/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data as ITariffPrices
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return null
    }
  }
  async signUpReport(period: TariffPeriod): Promise<ISignUpReportResponse | null> {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('No token')
    }
    const params = new URLSearchParams({
      period: period
    })
    try {
      const response = await fetch(`/api/tariff/save_card/?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data as ISignUpReportResponse
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return null
    }
  }
  async cancelReport(): Promise<ICancelTariffResponse> {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('No token')
    }

    try {
      const response = await fetch(`/api/tariff/cancel_report/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data as ICancelTariffResponse
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return { expiry_date: new Date('2000-01-01')}
    }
  }
  async reactivateReport(): Promise<boolean> {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('No token')
    }

    try {
      const response = await fetch(`/api/tariff/reactivate_report/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data.success
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return false
    }
  }
  async getUserTariff(): Promise<IUserTariffResponse> {
    const token = localStorage.getItem('token')
    try {
      const response = await fetch(`/api/user/tariff/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data as IUserTariffResponse
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return { tariff_code: "TRIAL", tariff_subscription_status: "ACTIVE" } as IUserTariffResponse
    }
  }
  async getPaymentStatus(invoice_id: number): Promise<IPaymentStatusResponse | null> {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('No token')
    }

    try {
      const response = await fetch(`/api/payment/status/${invoice_id}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data as IPaymentStatusResponse
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return null
    }
  }
  async getSseCookie(): Promise<boolean> {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('No token')
    }

    try {
      const response = await fetch(`/api/sse-auth`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data["status"]
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
      return false
    }
  }
}

const reportsClient = new ReportsClient();

export { reportsClient }
